import React, { useState, useRef } from 'react';
import { Form, Alert, Row, Col, Badge, ListGroup, Button } from 'react-bootstrap';
import { FaFileAlt, FaEye, FaSpinner, FaTrash, FaPaperclip, FaTimes, FaMicrophone, FaStop } from 'react-icons/fa';
import axios from 'axios';
import CustomRichTextEditor from '../CustomRichTextEditor';

const API_URL = process.env.REACT_APP_API_URL;

export default function ClinicalTab({
    editingNote,
    noteForm,
    isAssistant,
    isNoteDateToday,
    isNoteDateInFuture,
    CurrentSpecialForm,
    isSpecialFormActive,
    specialData,
    setSpecialData,
    handleDescriptionChange,
    useGPTRevision,
    setUseGPTRevision,
    existingFiles,
    filesToDelete,
    deletingFileId,
    handlePreview,
    toggleDeleteFile,
    selectedFiles,
    submitting,
    handleFileChange,
    removeFile,
    fileError,
}) {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const doctorId = userInfo.doctor?.doctor_id || null;
    const canUseVoiceInput = [25, 26, 43, 44, 371, 179].includes(doctorId);

    const startRecording = async () => {
        try {
            // Check if MediaRecorder is supported
            if (!window.MediaRecorder) {
                alert('Voice recording is not supported in this browser/webview. Please use Safari browser directly or update your iOS version.');
                console.error('MediaRecorder is not supported');
                return;
            }
            
            console.log('Requesting microphone access...');
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            console.log('Microphone access granted, stream:', stream);
            
            // Ensure stream is active
            if (!stream || !stream.active) {
                throw new Error('Stream is not active');
            }
            
            // Try to use a compatible format for iOS
            let options = {};
            
            // Check supported formats in order of preference for iOS
            if (MediaRecorder.isTypeSupported('audio/mp4')) {
                options = { mimeType: 'audio/mp4' };
                console.log('Using audio/mp4');
            } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                options = { mimeType: 'audio/webm' };
                console.log('Using audio/webm');
            } else if (MediaRecorder.isTypeSupported('audio/wav')) {
                options = { mimeType: 'audio/wav' };
                console.log('Using audio/wav');
            } else {
                console.log('No specific MIME type supported, using default');
            }
            
            console.log('Creating MediaRecorder with options:', options);
            const mediaRecorder = new MediaRecorder(stream, options);
            console.log('MediaRecorder created successfully');
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                console.log('Data available:', event.data.size);
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                console.log('Recording stopped');
                // Try to use a more compatible audio format
                const mimeType = mediaRecorder.mimeType || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                
                console.log('Audio recorded:', {
                    size: audioBlob.size,
                    type: audioBlob.type,
                    mimeType: mimeType
                });
                
                await transcribeAudio(audioBlob);
                
                // Stop all tracks to release microphone
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.onerror = (event) => {
                console.error('MediaRecorder error:', event.error);
                setIsRecording(false);
                alert('Recording error: ' + event.error);
            };

            console.log('Starting MediaRecorder...');
            mediaRecorder.start();
            console.log('MediaRecorder state:', mediaRecorder.state);
            setIsRecording(true);
            console.log('Recording started successfully');
        } catch (error) {
            console.error('Error accessing microphone:', error);
            console.error('Error name:', error.name);
            console.error('Error message:', error.message);
            setIsRecording(false);
            
            if (error.name === 'NotAllowedError') {
                alert('Microphone permission denied. Please allow microphone access and try again.');
            } else if (error.name === 'NotFoundError') {
                alert('No microphone found. Please check your device.');
            } else {
                alert('Could not access microphone: ' + error.message);
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        try {
            setIsTranscribing(true);

            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');

            console.log('Sending audio to Whisper API...', {
                size: audioBlob.size,
                type: audioBlob.type
            });

            const response = await axios.post(
                `${API_URL}whisper/transcribe`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Accept': 'application/json'
                    }
                }
            );

            console.log('Whisper API response:', response.data);

            const transcription = response.data.text || response.data.transcription || '';
            if (transcription) {
                const currentText = noteForm.description || '';
                const newText = currentText + (currentText ? ' ' : '') + transcription;
                handleDescriptionChange(newText);
            } else {
                alert('No transcription received. Please try again.');
            }
        } catch (error) {
            console.error('Transcription error:', error);
            console.error('Error response:', error.response?.data);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to transcribe audio. Please try again.';
            alert(errorMsg);
        } finally {
            setIsTranscribing(false);
        }
    };

    const toggleVoiceInput = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const canEditClinical = ((!editingNote && isNoteDateToday() && !isAssistant) || (editingNote && !isNoteDateInFuture()));
    const canUpload = ((!editingNote && isNoteDateToday()) || (editingNote && !isNoteDateInFuture()));

    if (!canEditClinical) {
        return <Alert variant="info">Clinical notes are available on the appointment day or later.</Alert>;
    }

    return (
        <>
            {isSpecialFormActive ? (
                <Form.Group className="mb-3">
                    <CurrentSpecialForm value={specialData} onChange={setSpecialData} disabled={false} />
                </Form.Group>
            ) : (
                <Form.Group className="mb-3">
                    <CustomRichTextEditor
                        value={noteForm.description || ''}
                        onChange={handleDescriptionChange}
                        placeholder="Enter note details..."
                        style={{ minHeight: '200px' }}
                    />
                    {canUseVoiceInput && (
                        <div className="mt-2">
                            <Button
                                variant={isRecording ? 'danger' : 'primary'}
                                size="sm"
                                onClick={toggleVoiceInput}
                                disabled={isTranscribing}
                                style={{
                                    borderRadius: '20px',
                                    padding: '8px 16px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    animation: isRecording ? 'pulse 1.5s infinite' : 'none'
                                }}
                                title={isRecording ? 'Stop recording' : 'Start recording'}
                            >
                                {isTranscribing ? (
                                    <>
                                        <FaSpinner className="fa-spin" size={14} />
                                        <span>Transcribing...</span>
                                    </>
                                ) : isRecording ? (
                                    <>
                                        <FaStop size={14} />
                                        <span>Stop Recording</span>
                                    </>
                                ) : (
                                    <>
                                        <FaMicrophone size={14} />
                                        <span>Voice Input</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                    {isRecording && (
                        <Alert variant="info" className="mt-2 py-2 mb-0 d-flex align-items-center">
                            <FaMicrophone className="me-2" />
                            <span>Recording... Click stop when finished</span>
                        </Alert>
                    )}
                    {isTranscribing && (
                        <Alert variant="warning" className="mt-2 py-2 mb-0 d-flex align-items-center">
                            <FaSpinner className="fa-spin me-2" />
                            <span>Transcribing audio with Whisper AI...</span>
                        </Alert>
                    )}
                </Form.Group>
            )}

            {editingNote && (noteForm.type === 'generic' || noteForm.type === 'appointment') && !isNoteDateInFuture() && !isAssistant && (
                <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa', borderColor: '#20c997' }}>
                    <Form.Check
                        type="checkbox"
                        id="useGPTRevision"
                        label="Use AI to organize and improve this note"
                        checked={useGPTRevision}
                        onChange={(e) => setUseGPTRevision(e.target.checked)}
                        className="mb-2"
                        style={{ fontSize: '16px', fontWeight: '500' }}
                    />
                </div>
            )}

            {editingNote && existingFiles.length > 0 && !isNoteDateInFuture() && (
                <Form.Group className="mb-3">
                    <Row className="g-2">
                        {existingFiles
                            .filter(file => !filesToDelete.includes(file.id))
                            .map((file) => {
                                const fileExt = file.original_filename?.split('.').pop().toLowerCase() || '';
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt);
                                return (
                                    <Col key={file.id} xs={4} sm={3} md={2}>
                                        <div className="border rounded p-1 h-100 d-flex flex-column align-items-center">
                                            {isImage ? (
                                                <div
                                                    className="position-relative"
                                                    style={{ width: '60px', height: '60px', overflow: 'hidden', cursor: 'pointer' }}
                                                    onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                                                    title="View image"
                                                >
                                                    <img
                                                        src={file.url}
                                                        alt={file.original_filename}
                                                        className="img-fluid"
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                                    <FaFileAlt className="text-muted" size={20} />
                                                    <small className="text-muted">{fileExt.toUpperCase()}</small>
                                                </div>
                                            )}
                                            <div className="w-100 text-center mt-1">
                                                <small className="text-truncate d-block" style={{ maxWidth: '100%' }} title={file.original_filename}>
                                                    {file.original_filename.split('.').shift().substring(0, 10)}...
                                                </small>
                                                <div className="d-flex justify-content-center gap-1 mt-1">
                                                    {isImage ? (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 text-primary"
                                                            onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                                                            title="View image"
                                                        >
                                                            <FaEye size={12} />
                                                        </button>
                                                    ) : (
                                                        <a
                                                            href={file.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-link p-0 text-primary"
                                                            onClick={(e) => e.stopPropagation()}
                                                            title="View file"
                                                        >
                                                            <FaEye size={12} />
                                                        </a>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-danger"
                                                        onClick={(e) => { e.stopPropagation(); toggleDeleteFile(file.id); }}
                                                        title="Delete file"
                                                        disabled={deletingFileId === file.id}
                                                    >
                                                        {deletingFileId === file.id ? (
                                                            <FaSpinner className="fa-spin" size={12} />
                                                        ) : (
                                                            <FaTrash size={12} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                    </Row>
                </Form.Group>
            )}

            {filesToDelete.length > 0 && (
                <div className="mb-3">
                    <small className="text-muted d-block mb-1">Files to be removed:</small>
                    <div className="d-flex flex-wrap gap-2">
                        {filesToDelete.map(fileId => {
                            const file = existingFiles.find(f => f.id === fileId);
                            if (!file) return null;
                            return (
                                <Badge key={fileId} bg="danger" className="d-flex align-items-center">
                                    <FaTrash className="me-1" size={10} />
                                    {file.original_filename}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white btn-sm ms-2"
                                        onClick={() => toggleDeleteFile(fileId)}
                                        aria-label="Undo delete"
                                    />
                                </Badge>
                            );
                        })}
                    </div>
                </div>
            )}

            {canUpload && (
                <Form.Group className="mb-3">
                    <div className="border rounded p-3 text-center">
                        <Form.Control
                            type="file"
                            multiple
                            accept="image/jpeg, image/png, image/gif, image/webp, video/mp4, video/webm, video/quicktime, video/x-m4v"
                            onChange={handleFileChange}
                            disabled={submitting}
                            className="d-none"
                            id="file-upload"
                        />
                        <label htmlFor="file-upload" className="btn btn-outline-primary d-inline-flex align-items-center">
                            <FaPaperclip className="me-2" />
                            Choose Files
                        </label>
                        <div className="mt-2 text-muted small">Max file size: 50MB. Allowed: images, videos, documents, archives</div>
                    </div>

                    {selectedFiles.length > 0 && (
                        <div className="mt-3">
                            <small className="d-block text-muted mb-1">New files to upload:</small>
                            <ListGroup>
                                {selectedFiles.map((file, index) => (
                                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <FaPaperclip className="me-2" />
                                            <span className="text-truncate" style={{ maxWidth: '200px' }}>{file.name}</span>
                                            <small className="text-muted ms-2">({(file.size / 1024 / 1024).toFixed(2)} MB)</small>
                                        </div>
                                        <Button
                                            variant="link"
                                            className="text-danger p-0"
                                            onClick={() => removeFile(index)}
                                            disabled={submitting}
                                            title="Remove file"
                                        >
                                            <FaTimes />
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {fileError && (
                        <Alert variant="danger" className="mt-2 py-2 mb-0">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            {fileError}
                        </Alert>
                    )}
                </Form.Group>
            )}
        </>
    );
}
